<script setup lang="js">
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import AppConfig from '@/shared/services/app_config';
import Session from '@/shared/services/session';
import { mapKeys, without, some, pick, snakeCase, pickBy, identity } from 'lodash-es';
import { I18n } from '@/i18n';
import Flash from '@/shared/services/flash';
import Records from '@/shared/services/records';
import EventBus from '@/shared/services/event_bus';
import AbilityService from '@/shared/services/ability_service';
import LmoUrlService from '@/shared/services/lmo_url_service';
import { addMinutes, intervalToDuration, format, formatDuration, addHours, isAfter } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { mdiCogOutline, mdiChevronDown, mdiChevronUp } from '@mdi/js';
import PollTemplateInfoPanel  from '@/components/poll_template/info_panel';
import { HandleDirective } from 'vue-slicksort';
import { useWatchRecords } from '@/composables/useWatchRecords';
import { useCurrentUserGroups } from '@/composables/useCurrentUserGroups';
import { pollReminderItems } from '@/shared/helpers/poll_reminder_items';

const props = defineProps({
  poll: Object,
  redirectOnSave: Boolean
});

const emit = defineEmits(['setPoll', 'saveSuccess']);

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

// url_for mixin functionality
const urlFor = (model, action, params) => LmoUrlService.route({model, action, params});
const mergeQuery = (obj) => ({query: pickBy(Object.assign({}, route.query, obj), identity)});

// watch_records composable
const { watchRecords } = useWatchRecords();
const { loadGroups } = useCurrentUserGroups();

// Template refs
const form = ref(null);

// Data
const loading = ref(false);
const newOption = ref(null);
const lastPollType = ref(props.poll.pollType);
const pollOptions = ref(props.poll.pollOptionsAttributes || props.poll.clonePollOptions());
const groupItems = ref([]);
const pollTemplate = ref(null);
const currentHideResults = ref(props.poll.hideResults);
const initialWeightedVoting = ref(props.poll.weightedVoting);
const hideResultsItems = ref([
  { title: I18n.global.t('poll_common_card.do_not_hide_results'), value: 'off' },
  { title: I18n.global.t('poll_common_card.until_you_vote'), value: 'until_vote' },
  { title: I18n.global.t('poll_common_card.until_voting_is_closed'), value: 'until_closed' }
]);
const currentTimeZone = computed(() => Session.user().timeZone);
const currentDate = format(utcToZonedTime(new Date(), currentTimeZone.value), 'yyyy-MM-dd');
const newDateOption = ref(zonedTimeToUtc(`${currentDate} 12:00`, currentTimeZone.value));
const minDate = ref(new Date());
const closingAtWas = ref(null);
const votingOpensImmediately = ref(!props.poll.openingAt);

const stvMethodItems = [
  { title: I18n.global.t('poll_stv_form.method_scottish'), value: 'scottish' },
  { title: I18n.global.t('poll_stv_form.method_meek'), value: 'meek' }
];
const stvQuotaItems = [
  { title: I18n.global.t('poll_stv_form.quota_droop'), value: 'droop' },
  { title: I18n.global.t('poll_stv_form.quota_hare'), value: 'hare' }
];

// Methods
const validate = (field) => {
  return [ () => props.poll.errors[field] === undefined || props.poll.errors[field][0] ];
};

const optionHasVotes = (option) => {
  return (props.poll.results.find(o => o.id === option.id) || {vote_count: 0}).vote_count > 0;
};

const setPollOptionPriority = () => {
  let i = 0;
  pollOptions.value.forEach(o => o.priority = 0);
  visiblePollOptions.value.forEach(o => o.priority = i++);
};

const removeOption = (option) => {
  if (optionHasVotes(option)) {
    if (!confirm(I18n.global.t("poll_common_form.option_has_votes_confirm_delete"))) { return; }
  }

  newOption.value = null;
  if (option.id) {
    option.name = '';
    option.meaning = '';
    option['_destroy'] = 1;
  } else {
    pollOptions.value = without(pollOptions.value, option);
  }
};

const addDateOption = () => {
  setTimeout(() => {
    const optionName = newDateOption.value.toJSON();
    if (some(pollOptions.value, o => o.name === optionName)) {
      Flash.error('poll_poll_form.option_already_added');
    } else {
      pollOptions.value.push({name: optionName});
    }
  });
};

const addOption = () => {
  const option = {
    name: '',
    meaning: '',
    prompt: '',
    icon: null,
    testOperator: null,
    testPercent: null,
    testAgainst: null
  };

  EventBus.$emit('openModal', {
    component: 'PollOptionForm',
    props: {
      pollOption: option,
      poll: props.poll,
      submitFn: option => {
        if (some(pollOptions.value, o => o.name.toLowerCase() === option.name.toLowerCase())) {
          Flash.error('poll_poll_form.option_already_added');
        } else {
          pollOptions.value.push(option);
        }
      }
    }
  });
};

const setAnonymousVoting = (value) => {
  if (!value) { return; }
  props.poll.weightedVoting = false;
  props.poll.hideResults = 'until_closed';
  props.poll.stanceReasonRequired = 'disabled';
  props.poll.notifyOnClosingSoon = 'voters_uncast';
};

const editOption = (option) => {
  const clone = pick(option, 'name', 'icon', 'meaning', 'prompt', 'testOperator', 'testPercent', 'testAgainst');

  EventBus.$emit('openModal', {
    component: 'PollOptionForm',
    props: {
      edit: true,
      pollOption: clone,
      poll: props.poll,
      submitFn: clone => {
        Object.assign(option, clone);
      }
    }
  });
};

const submit = () => {
  loading.value = true;
  const actionName = props.poll.isNew() ? 'created' : 'updated';
  props.poll.setErrors({});
  setPollOptionPriority();
  props.poll.pollOptionsAttributes = pollOptions.value.map((o) => mapKeys(o, (_, k) => k === '_destroy' ? k : snakeCase(k)))
  props.poll.save().then(data => {
    EventBus.$emit('deleteDraft', 'poll', props.poll.id, 'details');

    const poll = Records.polls.find(data.polls[0].id);
    initialWeightedVoting.value = poll.weightedVoting;
    if (props.redirectOnSave) { router.replace(urlFor(poll)); }
    emit('saveSuccess', poll);

    if (actionName == 'created') {
      Flash.success("poll_common_form.poll_type_started", {poll_type: poll.translatedPollTypeCaps()});
    } else {
      Flash.success("poll_common_form.poll_type_updated", {poll_type: poll.translatedPollTypeCaps()});
    }

    if ((actionName == 'created') && poll.specifiedVotersOnly && (poll.openedAt || poll.openingAt)) {
      EventBus.$emit('openModal', {
        component: 'PollMembers',
        persistent: false,
        props: { poll }
      });
    }
  }).catch(error => {
    form.value.validate();
    Flash.serverError(error, ['title', 'choiceCountMin', 'pointsPerVoter', 'closingAt', 'openingAt']);
  }).finally(() => loading.value = false);
};

// Computed
const titlePlaceholder = computed(() => {
  if (pollTemplate.value && pollTemplate.value.titlePlaceholder) {
    return I18n.global.t('common.prefix_eg', {val: pollTemplate.value.titlePlaceholder});
  } else {
    return I18n.global.t('poll_proposal_form.title_placeholder');
  }
});

const knownOptions = computed(() => {
  return (AppConfig.pollTypes[props.poll.pollType].common_poll_options || []);
});

const formattedDuration = computed(() => {
  if (!props.poll.meetingDuration) { return ''; }
  const minutes = parseInt(props.poll.meetingDuration);
  const duration = intervalToDuration({ start: new Date, end: addMinutes(new Date, minutes) });
  return formatDuration(duration, { format: ['hours', 'minutes'] });
});

const visiblePollOptions = computed(() => pollOptions.value.filter(o => !o._destroy));

const hasOptions = computed(() => props.poll.config().has_options);
const minOptions = computed(() => props.poll.config().min_options);
const allowAnonymous = computed(() => !props.poll.config().prevent_anonymous);
const allowWeightedVoting = computed(() => !props.poll.config().prevent_weighted_voting);
const willTurnOffWeightedVoting = computed(() => !props.poll.isNew() && initialWeightedVoting.value && !props.poll.weightedVoting);
const willTurnOnWeightedVoting = computed(() => !props.poll.isNew() && !initialWeightedVoting.value && props.poll.weightedVoting);

const stanceReasonRequiredItems = computed(() => [
  {title: I18n.global.t('poll_common_form.stance_reason_required'), value: 'required'},
  props.poll.pollType === 'proposal' && {title: I18n.global.t('poll_common_form.stance_reason_required_for_disagree_or_block'), value: 'required_for_disagree_or_block'},
  props.poll.pollType === 'proposal' && {title: I18n.global.t('poll_common_form.stance_reason_required_for_block'), value: 'required_for_block'},
  {title: I18n.global.t('poll_common_form.stance_reason_optional'), value: 'optional'},
  {title: I18n.global.t('poll_common_form.stance_reason_disabled'), value: 'disabled'}
].filter(Boolean));

const titlePath = computed(() => {
  return (props.poll.isNew() && 'action_dock.new_poll_type') || 'action_dock.edit_poll_type';
});

const titleVisible = (visible) => {
  if (props.redirectOnSave) { EventBus.$emit('content-title-visible', visible); }
};

const titleArgs = computed(() => {
  return {pollType: props.poll.translatedPollType().toLowerCase()};
});

const closesSoon = computed(() => {
  return !(props.poll.closingAt && isAfter(props.poll.closingAt, addHours(new Date(), 24)));
});

const closingSoonItems = computed(() => pollReminderItems(t));

const optionFormat = computed(() => props.poll.pollOptionNameFormat);
const hasOptionIcon = computed(() => props.poll.config().has_option_icon);

// Lifecycle
onMounted(() => {
  if (props.redirectOnSave) {
    EventBus.$emit('content-title-visible', false);
    EventBus.$emit('currentComponent', { title: I18n.global.t(titlePath.value, titleArgs.value), page: 'pollFormPage' });
  }
  loadGroups();

  Records.pollTemplates.findOrFetchByKeyOrId(props.poll.pollTemplateKeyOrId()).then(template => {
    pollTemplate.value = template;
  });

  watchRecords({
    collections: ['groups', 'memberships'],
    query: () => {
      return groupItems.value = [
        {title: I18n.global.t('discussion_form.none_direct_discussion'), value: null}
      ].concat(Session.user().groups().filter(g => AbilityService.canStartPoll(g)).map(g => ({
        title: g.fullName,
        value: g.id
      })));
    }
  });
});

// Expose handle directive for template
defineOptions({
  directives: { handle: HandleDirective }
});
</script>
<template lang="pug">
v-form.poll-common-form(ref="form" @submit.prevent="submit")
  v-card-title.px-0.pt-4.d-flex
    span(tabindex="-1" v-intersect="{handler: titleVisible}" v-t="{path: titlePath, args: titleArgs}")
    v-spacer

    v-btn(v-if="poll.id" icon variant="text" :to="urlFor(poll)" aria-hidden='true')
      common-icon(name="mdi-close")
    v-btn(v-if="!poll.id" icon variant="text" @click="$emit('setPoll', null)" aria-hidden='true')
      common-icon(name="mdi-close")

  poll-template-info-panel.mb-4(v-if="pollTemplate" :poll-template="pollTemplate")

  v-select.poll-common-form__group-select(
    v-if="!poll.topicId"
    v-model="poll.groupId"
    :items="groupItems"
    :label="t('common.group')"
  )

  v-text-field.poll-common-form-fields__title.mb-2(
    type='text'
    required='true'
    :hint="$t('poll_common_form.title_hint')"
    :placeholder="titlePlaceholder"
    :label="$t('poll_common_form.title')"
    v-model='poll.title'
    :rules="validate('title')"
    maxlength='250'
  )


  lmo-textarea.poll-common-form-fields__details(
    :model='poll'
    field="details"
    :placeholder="$t('poll_common_form.details_placeholder')"
    :label="$t('poll_common_form.details')"
  )

  template(v-if="hasOptions")
    v-divider.my-4
    .text-body-large.py-2.poll-common-form__options-heading( v-t="'poll_common_form.options'")
    //p.text-body-small.text-medium-emphasis(v-if="optionFormat == 'iso8601'" v-t="'poll_meeting_form.participants_see_local_times'")
    v-alert(v-if="!pollOptions.length" variant="tonal" type="info")
      span(v-t="'poll_common_form.no_options_add_some'")
    sortable-list(
      v-model:list="pollOptions"
      append-to=".app-is-booted"
      use-drag-handle
      lock-axis="y"
      v-if="pollOptions.length"
    )
      sortable-item(
        v-for="(option, priority) in visiblePollOptions"
        :index="priority"
        :key="option.name"
      )
        v-list-item.mb-2.poll-common-form__option(:class="option.icon && 'poll-common-form__option--' + option.icon" lines="two" rounded variant="tonal" style="user-select: none")
          template(v-slot:prepend v-if="hasOptionIcon" v-handle)
            v-avatar(size="48")
              img(:src="'/img/' + option.icon + '.svg?v=20260721'" aria-hidden="true")

          v-list-item-title(v-handle)
            span(v-if="optionFormat == 'i18n'" v-t="'poll_proposal_options.'+option.name")
            span(v-if="optionFormat == 'plain'") {{option.name}}
            span(v-if="optionFormat == 'iso8601'")
              poll-meeting-time(:name="option.name" :zone="currentTimeZone")
          v-list-item-subtitle.poll-common-vote-form__allow-wrap
            p.font-italic.mb-1(v-t="{path: `poll_option_form.${option.testOperator}_${option.testAgainst}`, args: {percent: option.testPercent} }" v-if="option.testOperator")
            p {{option.meaning}}

          template(v-slot:append)
            div.ml-0(v-if="poll.pollType != 'meeting'")
              v-btn.poll-common-form__edit-option(icon variant="text" @click="editOption(option)" :title="$t('common.action.edit')")
                common-icon(name="mdi-pencil")
            v-btn(
              icon
              variant="text"
              @click="removeOption(option)"
              :title="$t('common.action.delete')"
            )
              common-icon(name="mdi-delete")
            common-icon(name="mdi-drag-vertical" style="cursor: grab" v-handle :title="$t('common.action.move')" v-if="poll.pollType != 'meeting'")

    template(v-if="optionFormat == 'i18n'")
      p This poll cannot have new options added. (contact support if you see this message)

    template(v-if="optionFormat == 'plain'")
      .d-flex.justify-center
        v-btn.poll-common-form__add-option-btn.my-2(
          variant="tonal"
          color="primary"
          @click="addOption"
        )
          span(v-t="'poll_common_add_option.modal.title'")

    template(v-if="optionFormat == 'iso8601'")
      .text-body-large.py-2(v-t="'poll_poll_form.new_option'")
      .d-flex.align-center
        date-time-picker(:min="minDate" v-model="newDateOption" :time-zone="currentTimeZone")
        v-btn.poll-meeting-form__option-button.ml-4(
          color="primary" variant="tonal"
          @click='addDateOption()'
        )
          span(v-t="'poll_poll_form.add_option_placeholder'")
      .d-flex.align-center.mt-4
        v-text-field.flex-grow-1(
          :label="$t('poll_meeting_form.meeting_duration')"
          v-model="poll.meetingDuration"
          :hint="formattedDuration ? formattedDuration : null"
          type="number"
        )

  template(v-if="poll.pollType == 'count'")
    p.text-body-large.mb-2(v-t="'poll_count_form.agree_target_helptext'")
    .d-flex
      v-text-field.poll-common-form__agree-target(
        v-model="poll.agreeTarget"
        type="number"
        :step="1"
        :label="$t('poll_count_form.agree_target_label')"
      )

  template(v-if="poll.pollType == 'score'")
    v-divider.my-4
    p.mt-4.text-body-large.mb-4(v-t="'poll_common_form.define_minimum_and_maxmimum_score'")
    .d-flex
      v-text-field.poll-score-form__min.mr-2(
        v-model="poll.scoreMin"
        type="number"
        :step="1"
        :label="$t('poll_common.min_score')")
      v-text-field.poll-score-form__max(
        v-model="poll.scoreMax"
        type="number"
        :step="1"
        :label="$t('poll_common.max_score')")

  template(v-if="poll.pollType == 'poll'")
    v-divider.my-4
    p.mt-4.text-body-large.mb-4(v-t="'poll_common_form.how_many_options_can_a_voter_choose'")
    .d-flex
      v-text-field.poll-common-form__minimum-stance-choices.mr-2(
        v-model="poll.choiceCountMin"
        type="number"
        :step="1"
        :hint="$t('poll_common_form.choose_at_least')"
        :label="$t('poll_common_form.minimum_choices')")
      v-text-field.poll-common-form__maximum-stance-choices(
        v-model="poll.choiceCountMax"
        type="number"
        :step="1"
        :hint="$t('poll_common_form.choose_at_most')"
        :label="$t('poll_common_form.maximum_choices')")

  template(v-if="poll.pollType == 'ranked_choice'")
    v-divider.my-4
    p.mt-4.text-body-large.mb-2(v-t="'poll_ranked_choice_form.minimum_stance_choices_helptext'")
    .text-body-medium.pb-4.text-medium-emphasis(v-t="'poll_ranked_choice_form.how_many_ranking_positions_explained'")
    .d-flex.align-center(v-if="poll.pollType == 'ranked_choice'")
      v-text-field.lmo-number-input(
        v-model="poll.choiceCountMin"
        :label="$t('poll_ranked_choice_form.number_of_choices')"
        type="number"
        :min="1"
        :max="poll.pollOptionNames.length"
        :rules="validate('choiceCountMin')"
      )

  template(v-if="poll.pollType == 'stv'")
    v-divider.my-4
    p.mt-4.text-body-large.mb-2(v-t="'poll_stv_form.settings_title'")
    .text-body-medium.pb-4.text-medium-emphasis(v-t="'poll_stv_form.settings_helptext'")
    v-text-field.lmo-number-input(
      v-model="poll.stvSeats"
      :label="$t('poll_stv_form.seats_label')"
      type="number"
      :min="1"
      :max="poll.pollOptionNames.length - 1"
      :rules="validate('stvSeats')"
    )
    v-select.mt-2(
      v-model="poll.stvMethod"
      :items="stvMethodItems"
      :label="$t('poll_stv_form.method_label')"
      :rules="validate('stvMethod')"
    )
    v-select.mt-2(
      v-model="poll.stvQuota"
      :items="stvQuotaItems"
      :label="$t('poll_stv_form.quota_label')"
      :rules="validate('stvQuota')"
    )

  template(v-if="poll.pollType == 'dot_vote'")
    v-divider.my-4
    p.mt-4.text-body-large.mb-4(v-t="'poll_common_form.how_many_points_does_each_voter_have_to_allocate'")
    v-text-field(
      :label="$t('poll_dot_vote_form.dots_per_person')"
      type="number"
      min="1"
      v-model="poll.pointsPerVoter"
      :rules="validate('pointsPerVoter')"
    )

  template(v-if="poll.config().allow_none_of_the_above")
    v-divider.my-4
    v-checkbox.poll-settings-allow-none-of-the-above(
      hide-details
      v-model="poll.showNoneOfTheAbove"
      :label="$t('poll_common_settings.show_none_of_the_above')"
    )

  v-divider.my-4
  .text-body-large.pb-2.poll-common-form__duration-heading(v-t="'poll_common_form.voting_duration'")
  .text-body-medium.pb-4.text-medium-emphasis
    span {{ t('poll_common_form.voting_duration_hint') }}
    help-link.ml-1(path="user_manual/polls/settings#duration")

  template(v-if="poll.openedAt")
    poll-common-opening-at-field.pb-4(:poll="poll" disabled)
  template(v-else)
    v-checkbox.mt-2.poll-common-form__opens-immediately(
      hide-details
      v-model="votingOpensImmediately"
      :label="$t('poll_common_opening_at_field.voting_opens_immediately')"
      @update:modelValue="val => { if (val) poll.openingAt = null }"
    )
    poll-common-opening-at-field.pb-4(v-if="!votingOpensImmediately" :poll="poll")

  poll-common-closing-at-field.pb-4(:poll="poll" :min-date="poll.openingAt")

  v-divider.my-4
  .text-body-large.pb-2.poll-common-form__voters-heading(v-t="'poll_common_settings.who_can_vote'")
  v-radio-group(
    v-model="poll.specifiedVotersOnly"
    :disabled="!poll.closingAt"
  )
    v-radio(
      v-if="!poll.groupId"
      :value="false"
      :label="$t('poll_common_settings.specified_voters_only_false_discussion')")
    v-radio(
      v-if="poll.groupId"
      :value="false"
      :label="$t('poll_common_settings.specified_voters_only_false_group')")
    v-radio.poll-common-settings__specified-voters-only(
      :value="true"
      :label="$t('poll_common_settings.selected_people_only')")

  div(style="height: 64px" v-if="poll.specifiedVotersOnly")
    .text-body-medium.font-italic.text-medium-emphasis.mt-n4.py-4(
      v-t="{path: 'poll_common_settings.invite_people_next', args: {poll_type: poll.translatedPollType()}}")

  div(style="height: 64px" v-if="!poll.openedAt && !(poll.specifiedVotersOnly && votingOpensImmediately)")
    v-checkbox.mt-n4.pb-0(
      :label="$t('poll_common_form.notify_voters_when_voting_opens', {poll_type: poll.translatedPollType()})"
      v-model="poll.notifyOnOpen")

  v-expansion-panels.my-4(variant="accordion")
    v-expansion-panel
      v-expansion-panel-title.poll-common-form__more-settings(:expand-icon="mdiChevronDown" :collapse-icon="mdiChevronUp")
        v-icon.mr-2(size="small" :icon="mdiCogOutline")
        span(v-t="'poll_common_form.more_settings'")
      v-expansion-panel-text
        template(v-if="allowAnonymous")
          .poll-common-form__anonymous-voting-title.text-body-large.pb-2 {{ t('poll_common_form.anonymous_voting') }}
          .poll-common-form__anonymous-voting-explanation.text-body-medium.pb-2.text-medium-emphasis
            span {{ t('poll_common_form.anonymous_votes_stored_separately') }}
            help-link.ml-1(path="user_manual/polls/anonymous_voting")
          v-checkbox.poll-settings-anonymous(
            hide-label
            :disabled="!!poll.openedAt"
            v-model="poll.anonymous"
            @update:model-value="setAnonymousVoting"
            :label="$t('poll_common_form.votes_are_anonymous')")

        template(v-if="allowWeightedVoting")
          v-divider.mb-4(v-if="allowAnonymous")
          .poll-common-form__weighted-voting-title.text-body-large.pb-2 {{ t('poll_common_form.weighted_voting') }}
          .text-body-medium.pb-2.text-medium-emphasis
            span {{ t('poll_common_form.give_voters_different_vote_weights') }}
            help-link.ml-1(path="user_manual/polls/weighted_voting")
          v-checkbox.poll-settings-weighted-voting(
            hide-details
            :disabled="poll.anonymous"
            v-model="poll.weightedVoting"
            :label="t('poll_common_form.use_weighted_voting')")
          p.text-body-small.text-medium-emphasis.mt-1(v-if="poll.anonymous") {{ t('poll_common_form.weighted_voting_is_not_available_with_anonymous_voting') }}
          v-alert.mt-2(v-if="willTurnOffWeightedVoting" type="warning" variant="tonal" density="compact")
            span.mr-1(v-if="poll.openedAt") {{ t('poll_common_form.issued_vote_weights_will_change') }}
            span {{ t('poll_common_form.weighted_voting_off_warning') }}
          v-alert.mt-2(v-if="willTurnOnWeightedVoting" :type="poll.openedAt ? 'warning' : 'info'" variant="tonal" density="compact")
            span.mr-1(v-if="poll.openedAt") {{ t('poll_common_form.issued_vote_weights_will_change') }}
            span {{ t(poll.groupId ? 'poll_common_form.weighted_voting_on_group' : 'poll_common_form.weighted_voting_on_direct') }}

        template(v-if="poll.config().allow_quorum")
          v-divider.mb-4(v-if="allowAnonymous || allowWeightedVoting")
          .poll-common-form__quorum-title.text-body-large.pb-2 {{ t('poll_common_form.quorum') }}
          .text-body-medium.pb-4.text-medium-emphasis
            span {{ t(poll.weightedVoting ? 'poll_common_form.quorum_vote_weight_hint' : 'poll_common_form.quorum_hint') }}
            help-link.ml-1(path="user_manual/polls/quorum")
          v-number-input.poll-common-form__quorum.mb-4(
            v-model="poll.quorumPct"
            :label="t('poll_common_form.quorum')"
            :placeholder="t(poll.weightedVoting ? 'poll_common_form.quorum_vote_weight_placeholder' : 'poll_common_form.quorum_voters_placeholder')"
            :min="0"
            :max="100"
            autocomplete="off"
            control-variant="hidden"
          )
            template(v-slot:append-inner)
              span.mr-4 %
          .text-body-medium.mt-n4.pb-4.font-italic.text-medium-emphasis(
            v-if="poll.quorumPct && poll.pollType == 'proposal' && poll.config().allow_vote_share_requirement"
          ) {{ t('poll_common_form.quorum_tip_vote_share_requirement') }}

        v-divider.mb-4(v-if="allowAnonymous || allowWeightedVoting || poll.config().allow_quorum")
        .poll-common-form__reminder-title.text-body-large.pb-2(v-t="'poll_common_form.reminder_notification'")
        .text-body-medium.pb-4.text-medium-emphasis
          span {{ t('poll_common_form.reminder_helptext') }}
          help-link.ml-1(path="user_manual/polls/settings#reminder")
        p(v-if="poll.closingAt && closesSoon"
          v-t="{path: 'poll_common_settings.notify_on_closing_soon.voting_closes_too_soon', args: {pollType: poll.translatedPollType()}}")
        v-select.poll-common-settings__notify-on-closing-soon(
          :disabled="!poll.closingAt || poll.anonymous"
          :label="$t('poll_common_settings.notify_on_closing_soon.voting_title')"
          v-model="poll.notifyOnClosingSoon"
          :items="closingSoonItems")

        template(v-if="allowAnonymous")
          v-divider.mb-4
          .text-body-large.pb-2(v-t="'poll_common_card.hide_results'")
          .text-body-medium.pb-4.text-medium-emphasis
            span {{ t('poll_common_form.hide_results_description') }}
            help-link.ml-1(path="user_manual/polls/settings#hide-results")
          v-select.poll-common-settings__hide-results(
            :label="$t('poll_common_card.hide_results')"
            :items="hideResultsItems"
            v-model="poll.hideResults"
            :disabled="poll.anonymous || (!!poll.openedAt && currentHideResults == 'until_closed')"
          )

        template(v-if="poll.config().can_shuffle_options")
          v-divider.pb-4
          .text-body-large.pb-2(v-t="'poll_common_settings.shuffle_options'")
          .text-body-medium.pb-2.text-medium-emphasis(v-t="'poll_common_settings.reduce_bias_by_showing_options_in_random_order'")
          v-checkbox.poll-settings-shuffle-options(
            hide-details
            v-model="poll.shuffleOptions"
            :label="$t('poll_common_settings.show_options_in_random_order')")

        template(v-if="!poll.config().hide_reason_required")
          v-divider.pb-4
          .text-body-large.pb-2(v-t="'poll_common_form.vote_reason'")
          .text-body-medium.pb-4.text-medium-emphasis
            span {{ t('poll_common_form.vote_reason_description') }}
            help-link.ml-1(path="user_manual/polls/settings#vote-reason")
          v-select.poll-common-form__stance-reason-required(
            :disabled="poll.anonymous"
            :label="$t('poll_common_form.stance_reason_required_label')"
            :items="stanceReasonRequiredItems"
            v-model="poll.stanceReasonRequired"
          )

          v-text-field(
            v-if="poll.stanceReasonRequired != 'disabled' && !poll.config().per_option_reason_prompt"
            v-model="poll.reasonPrompt"
            :label="$t('poll_common_form.reason_prompt')"
            :hint="$t('poll_option_form.prompt_hint')"
            :placeholder="$t('poll_common.reason_placeholder')")

          v-checkbox(
            v-if="poll.stanceReasonRequired != 'disabled'"
            v-model="poll.limitReasonLength"
            :label="$t('poll_common_form.limit_reason_length')"
          )

        template(v-if="!poll.topicId")
          v-divider.mb-4
          .text-body-large.pb-2(v-t="'thread_arrangement_form.thread_settings'")
          topic-settings-fields(:model="poll" kind="poll")

        common-notify-fields(v-if="poll.id" :model="poll" includeActor)

  v-card-actions.poll-common-form-actions
    help-btn(path='en/user_manual/polls/intro_to_decisions')
    v-spacer

    v-btn.poll-common-form__submit(
      color="primary"
      type="submit"
      :loading="loading"
      :disabled="hasOptions && pollOptions.length < minOptions"
      variant="elevated"
    )
      span(v-if='poll.id' v-t="'common.action.save_changes'")
      span(v-if='!poll.id && poll.closingAt && votingOpensImmediately' v-t="{path: 'poll_common_form.start_poll_type', args: {poll_type: poll.translatedPollType()}}")
      span(v-if='!poll.id && poll.closingAt && !votingOpensImmediately' v-t="{path: 'poll_common_form.create_poll_type', args: {poll_type: poll.translatedPollType()}}")
      span(v-if='!poll.id && !poll.closingAt' v-t="'poll_common_form.save_poll'")

</template>
